import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  List,
  ListItem,
  ListItemText,
  Paper,
  Button,
  Alert
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import StepWizard from '../../components/common/StepWizard';
import { useAppContext } from '../../context/AppContext';

// Step 1: Basic Information
const Step1BasicInfo = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Basic Information
    </Typography>
    <TextField
      fullWidth
      label="Channel Name"
      value={data.name || ''}
      onChange={(e) => onDataChange({ name: e.target.value })}
      margin="normal"
      required
    />
    <TextField
      fullWidth
      label="Channel Description"
      value={data.description || ''}
      onChange={(e) => onDataChange({ description: e.target.value })}
      margin="normal"
      multiline
      rows={4}
      placeholder="Describe the purpose of this shopping assistant..."
    />
  </Box>
);

// Step 2: Select Products & Categories
const Step2Products = ({ data, onDataChange }) => {
  const { productCategories } = useAppContext();
  const selectedCategories = data.selectedCategories || [];

  const handleToggle = (categoryId) => {
    const currentIndex = selectedCategories.indexOf(categoryId);
    const newSelected = [...selectedCategories];

    if (currentIndex === -1) {
      newSelected.push(categoryId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onDataChange({ selectedCategories: newSelected });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Product Categories
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Choose which product categories the shopping agent can recommend
      </Typography>
      <FormGroup sx={{ mt: 2 }}>
        {productCategories.map((category) => (
          <FormControlLabel
            key={category.id}
            control={
              <Checkbox
                checked={selectedCategories.indexOf(category.id) !== -1}
                onChange={() => handleToggle(category.id)}
              />
            }
            label={`${category.name} (${category.productCount} products)`}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

// Step 3: Widget Appearance
const Step3Appearance = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Widget Appearance
    </Typography>

    {/* Widget Position */}
    <FormControl component="fieldset" margin="normal" fullWidth>
      <FormLabel>Widget Position</FormLabel>
      <RadioGroup
        value={data.position || 'bottom-right'}
        onChange={(e) => onDataChange({ position: e.target.value })}
        sx={{ mt: 1 }}
      >
        <FormControlLabel value="bottom-right" control={<Radio />} label="Bottom Right" />
        <FormControlLabel value="bottom-left" control={<Radio />} label="Bottom Left" />
        <FormControlLabel value="top-right" control={<Radio />} label="Top Right" />
        <FormControlLabel value="top-left" control={<Radio />} label="Top Left" />
      </RadioGroup>
    </FormControl>

    {/* Primary Color */}
    <TextField
      fullWidth
      label="Primary Color"
      value={data.primaryColor || '#1976d2'}
      onChange={(e) => onDataChange({ primaryColor: e.target.value })}
      margin="normal"
      placeholder="#1976d2"
      helperText="Hex color code for widget theme (e.g., #1976d2)"
    />

    {/* Agent Name */}
    <TextField
      fullWidth
      label="Agent Name"
      value={data.agentName || 'Shopping Assistant'}
      onChange={(e) => onDataChange({ agentName: e.target.value })}
      margin="normal"
      placeholder="Shopping Assistant"
    />

    {/* Greeting Message */}
    <TextField
      fullWidth
      label="Greeting Message"
      value={data.greetingMessage || ''}
      onChange={(e) => onDataChange({ greetingMessage: e.target.value })}
      margin="normal"
      placeholder="Hi! How can I help you find products today?"
      multiline
      rows={2}
    />
  </Box>
);

// Step 4: Product Recommendations
const Step4Recommendations = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Product Recommendations
    </Typography>

    <FormControl component="fieldset" margin="normal" fullWidth>
      <FormLabel>Recommendation Strategy</FormLabel>
      <RadioGroup
        value={data.strategy || 'best-sellers'}
        onChange={(e) => onDataChange({ strategy: e.target.value })}
        sx={{ mt: 1 }}
      >
        <FormControlLabel
          value="best-sellers"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="subtitle1">Best Sellers</Typography>
              <Typography variant="body2" color="textSecondary">
                Prioritize popular products
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          value="personalized"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="subtitle1">Personalized</Typography>
              <Typography variant="body2" color="textSecondary">
                AI-driven recommendations based on user behavior
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          value="category-based"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="subtitle1">Category-based</Typography>
              <Typography variant="body2" color="textSecondary">
                Match user queries to product categories
              </Typography>
            </Box>
          }
        />
      </RadioGroup>
    </FormControl>

    <TextField
      fullWidth
      type="number"
      label="Max Recommendations per Response"
      value={data.maxRecommendations || 3}
      onChange={(e) => onDataChange({ maxRecommendations: parseInt(e.target.value) || 3 })}
      margin="normal"
      inputProps={{ min: 1, max: 10 }}
      helperText="Number of products to recommend in each response (1-10)"
    />
  </Box>
);

// Step 5: Generate Embedding Code
const Step5EmbeddingCode = ({ data }) => {
  const channelId = `sa-${Date.now()}`;

  const embeddingCode = `<!-- Shopping Agent Widget -->
<script>
  (function() {
    var config = {
      channelId: '${channelId}',
      position: '${data.position || 'bottom-right'}',
      primaryColor: '${data.primaryColor || '#1976d2'}',
      greetingMessage: '${data.greetingMessage || 'Hi! How can I help you find products today?'}',
      agentName: '${data.agentName || 'Shopping Assistant'}',
      strategy: '${data.strategy || 'best-sellers'}',
      maxRecommendations: ${data.maxRecommendations || 3}
    };
    var script = document.createElement('script');
    script.src = 'https://cdn.example.com/shopping-agent.js';
    script.async = true;
    script.setAttribute('data-config', JSON.stringify(config));
    document.body.appendChild(script);
  })();
</script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embeddingCode);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Generate Embedding Code
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Copy the code below and paste it into your website's HTML, just before the closing &lt;/body&gt; tag.
      </Alert>

      <Typography variant="subtitle2" gutterBottom>
        Embedding Code
      </Typography>
      <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
        <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
          {embeddingCode}
        </pre>
      </Paper>
      <Button
        variant="outlined"
        startIcon={<ContentCopy />}
        onClick={handleCopy}
        sx={{ mb: 3 }}
      >
        Copy Embedding Code
      </Button>

      <Typography variant="subtitle2" gutterBottom>
        Integration Instructions
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary="1. Copy the embedding code above"
            secondary="Use the 'Copy Embedding Code' button"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="2. Paste into your website's HTML"
            secondary="Add the code just before the closing </body> tag"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="3. The widget will appear on your website"
            secondary={`Position: ${data.position || 'bottom-right'}`}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="4. Test the shopping assistant"
            secondary="Visit your website to see the widget in action"
          />
        </ListItem>
      </List>
    </Box>
  );
};

// Step 6: Review & Create
const Step6Review = ({ data }) => {
  const { productCategories } = useAppContext();
  const selectedCategories = data.selectedCategories || [];
  const selectedCategoryNames = productCategories
    .filter(cat => selectedCategories.includes(cat.id))
    .map(cat => cat.name);

  const strategyLabels = {
    'best-sellers': 'Best Sellers',
    'personalized': 'Personalized',
    'category-based': 'Category-based'
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Configuration
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="textSecondary">Channel Name</Typography>
        <Typography variant="body1" gutterBottom>{data.name || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Channel Description</Typography>
        <Typography variant="body1" gutterBottom>{data.description || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Selected Product Categories</Typography>
        <List dense>
          {selectedCategoryNames.length > 0 ? (
            selectedCategoryNames.map((name, index) => (
              <ListItem key={index}>
                <ListItemText primary={name} />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">No categories selected</Typography>
          )}
        </List>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Widget Position</Typography>
        <Typography variant="body1" gutterBottom>{data.position || 'bottom-right'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Primary Color</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              backgroundColor: data.primaryColor || '#1976d2',
              border: '1px solid #ccc',
              borderRadius: 1
            }}
          />
          <Typography variant="body1">{data.primaryColor || '#1976d2'}</Typography>
        </Box>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Agent Name</Typography>
        <Typography variant="body1" gutterBottom>{data.agentName || 'Shopping Assistant'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Greeting Message</Typography>
        <Typography variant="body1" gutterBottom>{data.greetingMessage || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Recommendation Strategy</Typography>
        <Typography variant="body1" gutterBottom>{strategyLabels[data.strategy] || 'Best Sellers'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Max Recommendations</Typography>
        <Typography variant="body1" gutterBottom>{data.maxRecommendations || 3} products per response</Typography>
      </Box>
    </Box>
  );
};

// Main ShoppingAgentWizard Component
const ShoppingAgentWizard = () => {
  const { addSalesChannel } = useAppContext();
  const navigate = useNavigate();

  const steps = [
    { label: 'Basic Information', component: <Step1BasicInfo /> },
    { label: 'Select Products', component: <Step2Products /> },
    { label: 'Widget Appearance', component: <Step3Appearance /> },
    { label: 'Recommendations', component: <Step4Recommendations /> },
    { label: 'Embedding Code', component: <Step5EmbeddingCode /> },
    { label: 'Review & Create', component: <Step6Review /> }
  ];

  const handleComplete = (formData) => {
    const channel = {
      name: formData.name,
      type: 'shopping-agent',
      typeName: 'Shopping Agent',
      config: formData
    };
    addSalesChannel(channel);
    navigate('/sales-channels');
  };

  return <StepWizard steps={steps} onComplete={handleComplete} title="Create Shopping Agent Channel" />;
};

export default ShoppingAgentWizard;
